import styled, { css } from 'styled-components';
import { getRadius, getSpace } from '../../../foundation/theme/space.utils';
import { getFontSize, getFontWeight } from '../../../foundation/theme/typography.utils';
import tokens from '../../../foundation/tokens/js/tokens';
import { MAPPING_MODE, MAPPING_SCALE } from './BaseSelect.constants';
// Styled-components.
// -----------------------------------------------------------------------------
export const BaseSelectWrapper = styled.div `
  ${({ mode: modeProps, scale: scaleProps, theme }) => {
    const mode = MAPPING_MODE[modeProps];
    const scale = MAPPING_SCALE[scaleProps];
    return css `
      .mf-select {
        /* Typing... */
        &__control {
          background-color: ${mode.control.background};
          border-radius: ${getRadius('m')};
          min-height: ${scale.control.minHeight};
          transition: 0.3s;

          /* Color */
          border: 2px solid ${mode.control.border};
          color: ${mode.control.border};

          /** Font */
          font-family: ${theme.typography.fontFamily};
          font-size: ${scale.control.fontSize};
          font-weight: ${getFontWeight('medium')};

          &:hover {
            border-color: ${mode.control.hover.border};
          }

          &--is-focused {
            outline: 0;
            border-color: ${mode.control.focus.border};
            box-shadow: 0 0 0 4px ${mode.control.focus.shadow};
          }
        }

        &__placeholder {
          color: ${mode.placeholder};
        }

        &__value-container {
          padding-left: ${scale.value.paddingLeft};
          padding-right: 0;
        }

        /* Arrow options */
        &__indicator-separator {
          display: none;
        }

        &__dropdown-indicator {
          background-image: url("data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.538289 0.730988H7.46171C7.94067 0.730988 8.18015 1.30951 7.84111 1.64855L4.38075 5.11161C4.17087 5.32149 3.82913 5.32149 3.61925 5.11161L0.158886 1.64855C-0.180154 1.30951 0.0593268 0.730988 0.538289 0.730988Z' fill='%23353241'/%3E%3C/svg%3E%0A");
          background-position: center;
          background-repeat: no-repeat;
          background-size: 0.65em auto;

          padding-bottom: ${scale.dropdownIndicator.paddingBottom};
          padding-left: ${scale.dropdownIndicator.paddingLeft};
          padding-right: ${scale.dropdownIndicator.paddingRight};
          padding-top: ${scale.dropdownIndicator.paddingTop};

          svg {
            display: none;
          }
        }

        &__control--menu-is-open .mf-select__dropdown-indicator {
          transform: rotate(180deg);
        }

        &__clear-indicator {
          border-right: 1px solid ${mode.clearIndicator.borderRight};
          color: ${mode.clearIndicator.color};
          margin-right: 0;
          padding: ${getSpace('xs')} ${getSpace('s')};

          svg {
            width: ${getSpace('l')};
            height: ${getSpace('l')};
          }
        }

        &__menu {
          border: 2px solid ${mode.menu.border};
          border-radius: ${getRadius('m')};
          box-shadow: ${tokens.shadows.light.xl.value};
          margin-top: ${getSpace('xs')};
          overflow: hidden;
          z-index: 2;

          .cs-limit-reached {
            padding: ${getSpace('l')};
          }
        }

        &__menu-list {
          padding-bottom: 0;
          padding-top: 0;
        }

        &__option {
          font-size: ${getFontSize('s')};
          font-weight: ${getFontWeight('medium')};
          padding: 20px;

          &--is-focused {
            background-color: ${mode.option.focus.background};
          }

          &--is-selected {
            background-color: ${mode.option.selected.background};
            color: ${mode.option.selected.color};
            font-weight: ${getFontWeight('black')};
          }
        }

        &__single-value,
        &__multi-value {
          margin-left: 0;
          margin-right: ${getSpace('xs')};
        }

        .multi-value-separator {
          display: inline-block;
          margin: 0 ${getSpace('xs')};
        }

        &__multi-value {
          background-color: ${mode.multi.value.background};
          border: 2px solid ${mode.multi.value.border};
          border-radius: 100px;

          &__label {
            padding-bottom: ${scale.multiLabel.paddingBottom};
            padding-left: ${scale.multiLabel.paddingLeft};
            padding-right: ${scale.multiLabel.paddingRight};
            padding-top: ${scale.multiLabel.paddingTop};

            font-size: ${getFontSize('s')};
            font-weight: ${getFontWeight('medium')};
            line-height: 1;
          }

          &__remove {
            padding-right: ${getSpace('s')};
            color: ${mode.multi.remove.color};

            &:hover {
              background: transparent;
              color: ${mode.multi.remove.hoverColor};
            }
          }
        }
      }

      .error {
        .mf-select__control {
          background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 0C3.58065 0 0 3.58065 0 8C0 12.4194 3.58065 16 8 16C12.4194 16 16 12.4194 16 8C16 3.58065 12.4194 0 8 0ZM11.9226 10.1C12.0742 10.2516 12.0742 10.4968 11.9226 10.6484L10.6452 11.9226C10.4935 12.0742 10.2484 12.0742 10.0968 11.9226L8 9.80645L5.9 11.9226C5.74839 12.0742 5.50323 12.0742 5.35161 11.9226L4.07742 10.6452C3.92581 10.4935 3.92581 10.2484 4.07742 10.0968L6.19355 8L4.07742 5.9C3.92581 5.74839 3.92581 5.50323 4.07742 5.35161L5.35484 4.07419C5.50645 3.92258 5.75161 3.92258 5.90323 4.07419L8 6.19355L10.1 4.07742C10.2516 3.92581 10.4968 3.92581 10.6484 4.07742L11.9258 5.35484C12.0774 5.50645 12.0774 5.75161 11.9258 5.90323L9.80645 8L11.9226 10.1Z' fill='%23DE492B'/%3E%3C/svg%3E%0A");
          background-repeat: no-repeat;
          background-position: right ${getSpace('l')} top 50%;
          border-color: ${tokens.colors.red[700].value};
          padding-right: 3rem;
        }
      }

      .success {
        .mf-select__control {
          background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 8C16 12.4183 12.4183 16 8 16C3.58171 16 0 12.4183 0 8C0 3.58171 3.58171 0 8 0C12.4183 0 16 3.58171 16 8ZM7.07464 12.2359L13.0101 6.30045C13.2117 6.0989 13.2117 5.7721 13.0101 5.57055L12.2802 4.84064C12.0787 4.63906 11.7519 4.63906 11.5503 4.84064L6.70968 9.68123L4.44971 7.42126C4.24816 7.21971 3.92135 7.21971 3.71977 7.42126L2.98987 8.15116C2.78832 8.35271 2.78832 8.67952 2.98987 8.88106L6.34471 12.2359C6.54629 12.4375 6.87306 12.4375 7.07464 12.2359Z' fill='%2378D549'/%3E%3C/svg%3E%0A");
          background-repeat: no-repeat;
          background-position: right ${getSpace('l')} top 50%;
          padding-right: 3rem;
        }
      }
    `;
}}
`;
//# sourceMappingURL=BaseSelect.styled.js.map